import { useState, useRef, useEffect } from 'react'
import { useConversation } from '@/hooks/useConversation'
import { useNotifications } from '@/hooks/useNotifications'
import ChatMessage from './ChatMessage'
import ChatInput from './ChatInput'
import ChatHeader from './ChatHeader'
import StatusPanel from '../StatusPanel/StatusPanel'
import NotificationToast from '../Notifications/NotificationToast'

interface ChatWidgetProps {
  conversationId?: string
  onConversationCreate?: (conversation: any) => void
  onConversationUpdate?: (conversation: any) => void
  showStatus?: boolean
  initialMessage?: string
}

export default function ChatWidget({ 
  conversationId: initialConversationId, 
  onConversationCreate,
  onConversationUpdate,
  showStatus = true,
  initialMessage = ''
}: ChatWidgetProps) {
  const [conversationId, setConversationId] = useState<string | undefined>(initialConversationId)
  const [conversationalMode, setConversationalMode] = useState(true)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const { notifications, removeNotification, showSuccess, showError, showInfo } = useNotifications()

  const {
    conversation,
    isLoading,
    isConnected,
    sendMessage,
    createConversation,
    isSending,
    isCreating,
  } = useConversation(conversationId)

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [conversation?.messages])

  // Notify parent when conversation updates
  useEffect(() => {
    if (conversation && onConversationUpdate) {
      console.log('Notifying parent of conversation update, has BPMN:', !!conversation.bpmn)
      onConversationUpdate(conversation)
    }
  }, [conversation, onConversationUpdate])


  const handleNewConversation = async () => {
    try {
      showInfo('Creating new conversation...')
      const newConversation = await createConversation(conversationalMode)
      setConversationId(newConversation.id)
      onConversationCreate?.(newConversation)
      showSuccess('Conversation created successfully!')
    } catch (error: any) {
      console.error('Failed to create conversation:', error)
      showError(`Failed to create conversation: ${error.message}`)
    }
  }

  const handleSendMessage = async (message: string) => {
    console.log('handleSendMessage called:', { conversationId, message })
    if (!message.trim()) {
      console.log('Skipping send - empty message')
      return
    }
    
    try {
      // Create conversation if it doesn't exist
      let activeConversationId = conversationId
      if (!activeConversationId) {
        console.log('No conversation exists, creating one...')
        showInfo('Creating conversation...')
        const newConversation = await createConversation(conversationalMode)
        setConversationId(newConversation.id)
        activeConversationId = newConversation.id
        onConversationCreate?.(newConversation)
      }
      
      // Send message using the hook's sendMessage with the active conversation ID
      await sendMessage(message, activeConversationId)
      console.log('Message sent successfully')
    } catch (error: any) {
      console.error('Error sending message:', error)
      showError(`Failed to send message: ${error.message}`)
    }
  }

  const handleToggleMode = (checked: boolean) => {
    setConversationalMode(checked)
    localStorage.setItem('conversationalMode', String(checked))
  }

  return (
    <div className="h-full flex relative">
      {/* Notifications */}
      <div className="absolute top-4 right-4 z-50 space-y-2">
        {notifications.map(notification => (
          <NotificationToast
            key={notification.id}
            message={notification.message}
            type={notification.type}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
      
      <div className="flex-1 flex flex-col bg-white rounded-lg shadow-lg overflow-hidden">
        <ChatHeader
          isConnected={isConnected}
          conversationalMode={conversationalMode}
          onToggleMode={handleToggleMode}
          onNewConversation={handleNewConversation}
        />

        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-2">
          {isLoading && (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0066CC]"></div>
            </div>
          )}

          {!isLoading && conversation?.messages?.length === 0 && (
            <div className="text-center text-gray-500 py-8">
              <p>Start a conversation by typing a message below.</p>
              <p className="text-sm mt-2">You can describe your workflow requirements in natural language.</p>
            </div>
          )}

          {conversation?.messages?.map((message, index) => (
            <ChatMessage key={message.id || index} message={message} />
          ))}

          {isSending && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-lg p-3 max-w-md">
                <div className="flex space-x-2">
                  <div className="animate-pulse w-2 h-2 bg-gray-400 rounded-full"></div>
                  <div className="animate-pulse w-2 h-2 bg-gray-400 rounded-full"></div>
                  <div className="animate-pulse w-2 h-2 bg-gray-400 rounded-full"></div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <ChatInput
          onSendMessage={handleSendMessage}
          disabled={isSending || isCreating}
          placeholder={
            isSending
              ? 'Sending...'
              : isCreating
              ? 'Creating conversation...'
              : 'Type your workflow description...'
          }
          initialValue={initialMessage}
        />
      </div>

      {showStatus && conversation && (
        <StatusPanel
          conversation={conversation}
          isConnected={isConnected}
        />
      )}
    </div>
  )
}